import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeftIcon, Bars3Icon, ChevronDownIcon, TrashIcon, XMarkIcon } from '@heroicons/react/24/solid';
import { apiUrl, authFetch, authService, getFileUrl, getImageUrl, normalizeLessonType, questionApi, toApiLessonType } from '../utils/api';

interface Question {
  text: string;
  type: 'MultipleChoice' | 'TrueFalse';
  imagePath?: string | null;
  answers: {
    text: string;
    isCorrect: boolean;
  }[];
}

type ChoicePreset = 'none' | 'arabic' | 'abcd';

const CHOICE_PRESET_OPTIONS: Record<Exclude<ChoicePreset, 'none'>, string[]> = {
  arabic: ['أ', 'ب', 'ت', 'ث'],
  abcd: ['A', 'B', 'C', 'D'],
};

function moveItem<T>(items: T[], from: number, to: number): T[] {
  if (from === to || from < 0 || to < 0 || from >= items.length || to >= items.length) {
    return items;
  }
  const next = [...items];
  const [moved] = next.splice(from, 1);
  next.splice(to, 0, moved);
  return next;
}

const answersForChoicePreset = (
  preset: ChoicePreset,
  previous: Question['answers'] = []
): Question['answers'] => {
  const texts = preset === 'none' ? ['', '', '', ''] : CHOICE_PRESET_OPTIONS[preset];
  return texts.map((text, index) => ({
    text,
    isCorrect: previous[index]?.isCorrect ?? false
  }));
};

interface LessonData {
  id: number;
  lessonName: string;
  titel?: string;
  type: string;
  videoUrl?: string;
  attachmentUrl?: string;
  attachmentTitel?: string;
  filePath1?: string | null;
  filePath2?: string | null;
  isUploaded?: boolean;
  active: boolean;
  order: number;
  unitId: number;
  questions?: Question[];
}

const LessonView = () => {
  const { courseId, lessonId } = useParams<{ courseId: string; lessonId: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lessonData, setLessonData] = useState<LessonData | null>(null);
  const [questions, setQuestions] = useState<Question[]>([]);
  const [isEditing, setIsEditing] = useState(true);
  const [editedData, setEditedData] = useState<Partial<LessonData>>({});
  const [saveMessage, setSaveMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);
  const [showQuestionForm, setShowQuestionForm] = useState(false);
  const [newQuestion, setNewQuestion] = useState<Question>({
    text: '',
    type: 'MultipleChoice',
    imagePath: null,
    answers: [
      { text: '', isCorrect: false },
      { text: '', isCorrect: false },
      { text: '', isCorrect: false },
      { text: '', isCorrect: false }
    ]
  });
  const [questionImagePreview, setQuestionImagePreview] = useState<string | null>(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [choicePreset, setChoicePreset] = useState<ChoicePreset>('none');
  const [draggedQuestionIndex, setDraggedQuestionIndex] = useState<number | null>(null);
  const [dragOverQuestionIndex, setDragOverQuestionIndex] = useState<number | null>(null);
  const [openQuestionIndex, setOpenQuestionIndex] = useState<number | null>(null);
  const questionDragged = useRef(false);

  // Handle input changes
  const handleInputChange = (field: keyof LessonData, value: string | number) => {
    setEditedData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Handle save changes
  const handleSave = async () => {
    if (!lessonData) return;

    try {
      const currentQuestions = questions.length > 0 ? questions : (lessonData.questions || []);
      
      const response = await authFetch(apiUrl(`Lesson/${lessonId}`), {
        method: 'PUT',
        headers: {
          'accept': '*/*',
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authService.getToken()}`
        },
        body: JSON.stringify({
          lessonName: editedData.lessonName || lessonData.lessonName,
          titel: editedData.lessonName || lessonData.lessonName, // Using lessonName as titel
          unitId: lessonData.unitId,
          active: lessonData.active,
          order: editedData.order || lessonData.order,
          type: toApiLessonType(lessonData.type),
          videoUrl: editedData.videoUrl || lessonData.videoUrl,
          filePath1: editedData.filePath1 !== undefined ? editedData.filePath1 : lessonData.filePath1,
          filePath2: editedData.filePath2 !== undefined ? editedData.filePath2 : lessonData.filePath2,
          isUploaded: editedData.isUploaded !== undefined ? editedData.isUploaded : lessonData.isUploaded,
          attachmentTitel: editedData.attachmentTitel || lessonData.attachmentTitel,
          attachmentUrl: editedData.attachmentUrl || lessonData.attachmentUrl,
          questions: currentQuestions.map((q, index) => ({
            text: q.text,
            type: q.type,
            imagePath: q.imagePath ?? null,
            order: index + 1,
            answers: q.answers
          }))
        })
      });

      const result = await response.json();

      if (result.success) {
        setSaveMessage({ type: 'success', text: 'تم حفظ التغييرات بنجاح!' });
        setLessonData(prev => ({
          ...prev!,
          ...editedData,
          questions: currentQuestions
        }));
        setIsEditing(false);
        setEditedData({});

        // Clear success message after 3 seconds
        setTimeout(() => {
          setSaveMessage(null);
        }, 3000);
      } else {
        throw new Error(result.message || 'فشل في حفظ التغييرات');
      }
    } catch (error) {
      console.error('Error saving changes:', error);
      setSaveMessage({ type: 'error', text: 'فشل في حفظ التغييرات. يرجى المحاولة مرة أخرى.' });
    }
  };

  // Function to add a new question
  const handleAddQuestion = () => {
    if (newQuestion.text.trim() === '') {
      setSaveMessage({ type: 'error', text: 'نص السؤال لا يمكن أن يكون فارغاً' });
      return;
    }

    // For TrueFalse questions, ensure there are exactly 2 answers: True and False
    let processedQuestion = { ...newQuestion };
    if (newQuestion.type === 'TrueFalse') {
      processedQuestion.answers = [
        { text: 'True', isCorrect: newQuestion.answers.find(a => a.isCorrect)?.text === 'True' || false },
        { text: 'False', isCorrect: newQuestion.answers.find(a => a.isCorrect)?.text === 'False' || false }
      ];
    } else {
      // For MultipleChoice, filter out empty answers
      processedQuestion.answers = newQuestion.answers.filter(a => a.text.trim() !== '');
      
      // Ensure at least one answer is marked as correct
      if (!processedQuestion.answers.some(a => a.isCorrect)) {
        setSaveMessage({ type: 'error', text: 'يجب تحديد إجابة واحدة على الأقل كصحيحة' });
        return;
      }
    }

    if (uploadingImage) {
      setSaveMessage({ type: 'error', text: 'يرجى الانتظار حتى يكتمل رفع الصورة' });
      return;
    }

    processedQuestion.imagePath = newQuestion.imagePath ?? null;

    // Add the new question to the questions array
    const updatedQuestions = [...questions, processedQuestion];
    setQuestions(updatedQuestions);
    
    // Reset form
    setNewQuestion({
      text: '',
      type: 'MultipleChoice',
      imagePath: null,
      answers: answersForChoicePreset(choicePreset)
    });
    setQuestionImagePreview(null);
    
    setShowQuestionForm(false);
    setSaveMessage({ type: 'success', text: 'تم إضافة السؤال بنجاح!' });
    
    // Clear success message after 3 seconds
    setTimeout(() => {
      setSaveMessage(null);
    }, 3000);
  };

  // Handle question input changes
  const handleQuestionChange = (field: keyof Question, value: string | 'MultipleChoice' | 'TrueFalse') => {
    setNewQuestion(prev => ({
      ...prev,
      [field]: value
    }));

    // If type changes to TrueFalse, reset answers
    if (field === 'type' && value === 'TrueFalse') {
      setNewQuestion(prev => ({
        ...prev,
        answers: [
          { text: 'True', isCorrect: false },
          { text: 'False', isCorrect: false }
        ]
      }));
    } else if (field === 'type' && value === 'MultipleChoice') {
      setNewQuestion(prev => ({
        ...prev,
        answers: answersForChoicePreset(choicePreset)
      }));
    }
  };

  const applyChoicePreset = (preset: Exclude<ChoicePreset, 'none'>) => {
    setChoicePreset(preset);
    setNewQuestion(prev => {
      if (prev.type !== 'MultipleChoice') return prev;
      return {
        ...prev,
        answers: answersForChoicePreset(preset, prev.answers)
      };
    });
  };

  // Handle answer changes
  const handleAnswerChange = (index: number, field: 'text' | 'isCorrect', value: string | boolean) => {
    setNewQuestion(prev => {
      const updatedAnswers = [...prev.answers];
      updatedAnswers[index] = {
        ...updatedAnswers[index],
        [field]: value
      };

      // For TrueFalse questions, ensure only one answer is correct
      if (prev.type === 'TrueFalse' && field === 'isCorrect' && value === true) {
        updatedAnswers.forEach((answer, i) => {
          if (i !== index) {
            updatedAnswers[i] = { ...answer, isCorrect: false };
          }
        });
      }

      return {
        ...prev,
        answers: updatedAnswers
      };
    });
  };

  const handleImageUpload = async (file: File) => {
    setUploadingImage(true);
    try {
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
      if (!allowedTypes.includes(file.type)) {
        setQuestionImagePreview(null);
        setSaveMessage({ type: 'error', text: 'صيغة الصورة غير مدعومة. المسموح: JPG, JPEG, PNG, GIF, WebP' });
        return;
      }

      if (file.size > 3145728) {
        setQuestionImagePreview(null);
        setSaveMessage({ type: 'error', text: 'حجم الملف يتجاوز الحد المسموح (3 ميجابايت)' });
        return;
      }

      const response = await questionApi.uploadQuestionImage(file);
      const imagePath = response?.imagePath || response?.data?.imagePath || response?.ImagePath;

      if (imagePath) {
        setNewQuestion(prev => ({ ...prev, imagePath }));
        setSaveMessage({ type: 'success', text: 'تم رفع الصورة بنجاح' });
        setTimeout(() => setSaveMessage(null), 3000);
      } else {
        setQuestionImagePreview(null);
        setSaveMessage({ type: 'error', text: response?.message || 'فشل رفع الصورة' });
      }
    } catch (error) {
      console.error('Error uploading image:', error);
      setQuestionImagePreview(null);
      setSaveMessage({ type: 'error', text: 'حدث خطأ أثناء رفع الصورة' });
    } finally {
      setUploadingImage(false);
    }
  };

  const handleImageFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onloadend = () => {
      setQuestionImagePreview(reader.result as string);
    };
    reader.readAsDataURL(file);
    handleImageUpload(file);
  };

  const handleRemoveImage = () => {
    setQuestionImagePreview(null);
    setNewQuestion(prev => ({ ...prev, imagePath: null }));
  };

  const handleQuestionDragStart = (event: React.DragEvent<HTMLDivElement>, index: number) => {
    questionDragged.current = true;
    event.dataTransfer.setData('text/plain', String(index));
    event.dataTransfer.effectAllowed = 'move';
    window.setTimeout(() => setDraggedQuestionIndex(index), 0);
  };

  const handleQuestionDragOver = (event: React.DragEvent<HTMLDivElement>, index: number) => {
    if (!isEditing) return;
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
    if (dragOverQuestionIndex !== index) setDragOverQuestionIndex(index);
  };

  const handleQuestionDrop = (event: React.DragEvent<HTMLDivElement>, dropIndex: number) => {
    event.preventDefault();
    const from = Number(event.dataTransfer.getData('text/plain'));
    setDraggedQuestionIndex(null);
    setDragOverQuestionIndex(null);
    if (!isEditing || Number.isNaN(from) || from === dropIndex) return;

    setQuestions(prev => moveItem(prev, from, dropIndex));
    setOpenQuestionIndex(current => {
      if (current === null) return null;
      if (current === from) return dropIndex;
      if (from < current && dropIndex >= current) return current - 1;
      if (from > current && dropIndex <= current) return current + 1;
      return current;
    });
    setSaveMessage({ type: 'success', text: 'تم تغيير ترتيب الأسئلة. انقر على حفظ لتطبيق التغييرات.' });
  };

  const handleQuestionDragEnd = () => {
    setDraggedQuestionIndex(null);
    setDragOverQuestionIndex(null);
    window.setTimeout(() => {
      questionDragged.current = false;
    }, 150);
  };

  // Function to delete a question
  const handleDeleteQuestion = (questionIndex: number) => {
    if (window.confirm('هل أنت متأكد من حذف هذا السؤال؟')) {
      const updatedQuestions = questions.filter((_, index) => index !== questionIndex);
      setQuestions(updatedQuestions);
      setOpenQuestionIndex(current => {
        if (current === null) return null;
        if (current === questionIndex) return null;
        if (current > questionIndex) return current - 1;
        return current;
      });
      setSaveMessage({ type: 'success', text: 'تم حذف السؤال. انقر على حفظ لتطبيق التغييرات.' });
      
      // Clear success message after 3 seconds
      setTimeout(() => {
        setSaveMessage(null);
      }, 3000);
    }
  };

  // Function to convert YouTube URL to embed URL
  const getYouTubeEmbedUrl = (url: string) => {
    if (!url) return '';
    
    // Handle different YouTube URL formats
    let videoId = '';
    
    // Format: https://youtube.com/watch?v=VIDEO_ID
    const watchUrlMatch = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\s]+)/);
    if (watchUrlMatch) {
      videoId = watchUrlMatch[1];
    }
    
    // Format: https://youtube.com/embed/VIDEO_ID
    const embedUrlMatch = url.match(/youtube\.com\/embed\/([^&\s]+)/);
    if (embedUrlMatch) {
      videoId = embedUrlMatch[1];
    }
    
    // If we found a video ID, return the embed URL
    return videoId ? `https://www.youtube.com/embed/${videoId}` : url;
  };

  useEffect(() => {
    const fetchLessonData = async () => {
      setLoading(true);
      setError(null);

      try {
        console.log('Fetching lesson data for ID:', lessonId);
        const lessonResponse = await authFetch(apiUrl(`Lesson/${lessonId}`), {
          method: 'GET',
          headers: {
            'accept': '*/*',
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authService.getToken()}`
          }
        });

        if (!lessonResponse.ok) {
          throw new Error(`HTTP error! status: ${lessonResponse.status}`);
        }

        const responseText = await lessonResponse.text();
        console.log('Raw response:', responseText);

        let lessonResult;
        try {
          lessonResult = JSON.parse(responseText);
        } catch (e) {
          console.error('JSON parse error:', e);
          throw new Error('Invalid JSON response from server');
        }

        if (!lessonResult.success) {
          throw new Error(lessonResult.message || 'فشل في جلب بيانات الدرس');
        }

        console.log('Parsed lesson data:', lessonResult.data);

        // Normalize the data
        const normalizedData = {
          ...lessonResult.data,
          type: normalizeLessonType(lessonResult.data.type),
          filePath1: lessonResult.data.filePath1 ?? lessonResult.data.FilePath1 ?? null,
          filePath2: lessonResult.data.filePath2 ?? lessonResult.data.FilePath2 ?? null,
          isUploaded: lessonResult.data.isUploaded ?? lessonResult.data.IsUploaded ?? true,
          questions: Array.isArray(lessonResult.data.questions) ? lessonResult.data.questions : []
        };

        console.log('Normalized data:', normalizedData);
        setLessonData(normalizedData);
        
        // Only set questions if they exist and are in the correct format
        if (Array.isArray(normalizedData.questions) && normalizedData.questions.length > 0) {
          setQuestions(normalizedData.questions);
        }

      } catch (error) {
        console.error('Error in fetchLessonData:', error);
        setError(error instanceof Error ? error.message : 'فشل في تحميل بيانات الدرس. يرجى المحاولة مرة أخرى لاحقاً.');
      } finally {
        setLoading(false);
      }
    };

    if (lessonId) {
      fetchLessonData();
    }
  }, [lessonId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex justify-center items-center" dir="rtl">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error || !lessonData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-8" dir="rtl">
        <div className="max-w-4xl mx-auto bg-white rounded-lg shadow-lg p-6">
          <div className="flex items-center mb-6">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center text-blue-600 hover:text-blue-800"
            >
              <ArrowLeftIcon className="h-5 w-5 ml-2" />
              رجوع
            </button>
          </div>
          <div className="text-center text-red-600">
            {error || 'لم يتم العثور على بيانات الدرس'}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-8" dir="rtl">
      <div className="max-w-4xl mx-auto bg-white rounded-lg shadow-lg p-6">
        {/* Header with back button */}
        <div className="flex items-center justify-between mb-6">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center text-blue-600 hover:text-blue-800"
          >
            <ArrowLeftIcon className="h-5 w-5 ml-2" />
            رجوع
          </button>
          {isEditing ? (
            <div className="space-x-reverse space-x-2">
              <button
                onClick={handleSave}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
              >
                حفظ
              </button>
              <button
                onClick={() => {
                  setIsEditing(false);
                  setEditedData({});
                }}
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300"
              >
                إلغاء
              </button>
            </div>
          ) : (
            <button
              onClick={() => setIsEditing(true)}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              تعديل
            </button>
          )}
        </div>

        {/* Save message */}
        {saveMessage && (
          <div
            className={`mb-4 p-3 rounded ${
              saveMessage.type === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
            }`}
          >
            {saveMessage.text}
          </div>
        )}

        {/* Lesson details */}
        <div className="space-y-6">
          <div>
            <h1 className="text-2xl font-bold mb-2">
                  {isEditing ? (
                    <input
                      type="text"
                      value={editedData.lessonName || lessonData.lessonName}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleInputChange('lessonName', e.target.value)}
                  className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  ) : (
                lessonData.lessonName
                  )}
            </h1>
                </div>

          {/* Video content */}
          {(lessonData.type.toLowerCase() === 'video' || lessonData.type === 'Video') && (
            <div>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    رابط الفيديو
                  </label>
                    <input
                    type="text"
                    value={editedData.videoUrl || lessonData.videoUrl || ''}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleInputChange('videoUrl', e.target.value)}
                    className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="أدخل رابط فيديو يوتيوب"
                  />
                </div>
              </div>
            </div>
          )}

          {normalizeLessonType(lessonData.type) === 'file' && (
            <div className="space-y-4">
              <div className="flex items-center gap-3 p-3 bg-gray-50 border border-gray-200 rounded-lg">
                <input
                  type="checkbox"
                  id="lessonPdfIsUploaded"
                  checked={editedData.isUploaded !== undefined ? editedData.isUploaded : (lessonData.isUploaded ?? true)}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    if (!isEditing) return;
                    setEditedData(prev => ({ ...prev, isUploaded: e.target.checked }));
                  }}
                  disabled={!isEditing}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label htmlFor="lessonPdfIsUploaded" className="text-sm font-medium text-gray-700">
                  يمكن للطلاب تحميل الملف
                </label>
              </div>

              {[1, 2].map((slot) => {
                const field = slot === 1 ? 'filePath1' : 'filePath2';
                const storedPath = editedData[field] !== undefined ? editedData[field] : lessonData[field];
                const href = getFileUrl(storedPath || '');
                return (
                  <div key={field}>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {slot === 1 ? 'ملف PDF الأول' : 'ملف PDF الثاني (اختياري)'}
                    </label>
                    <div className="border rounded-lg p-4 bg-gray-50 space-y-2">
                      {href && (
                        <a
                          href={href}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:text-blue-800 flex items-center"
                        >
                          <svg className="w-6 h-6 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                          </svg>
                          {slot === 1 ? 'عرض / تحميل الملف الأول' : 'عرض / تحميل الملف الثاني'}
                        </a>
                      )}
                      {isEditing && (
                        <input
                          type="url"
                          value={storedPath || ''}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleInputChange(field, e.target.value)}
                          className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="https://example.com/document.pdf"
                        />
                      )}
                      {!href && !isEditing && (
                        <p className="text-sm text-gray-500">لا يوجد ملف</p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Quiz content */}
          {(lessonData.type.toLowerCase() === 'quiz' || lessonData.type === 'Quiz') && (
            <div className="space-y-6">
              <div className="flex justify-between items-center">
              <h2 className="text-xl font-semibold text-gray-900">أسئلة الاختبار</h2>
                {isEditing && (
                  <button
                    onClick={() => setShowQuestionForm(true)}
                    className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 flex items-center"
                  >
                    <span className="ml-1">+</span> إضافة سؤال
                  </button>
                )}
              </div>
              
              {/* Question Form */}
              {showQuestionForm && isEditing && (
                <div className="bg-gray-50 p-6 rounded-lg border border-gray-200 shadow-sm">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">إضافة سؤال جديد</h3>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        نص السؤال
                      </label>
                      <input
                        type="text"
                        value={newQuestion.text}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleQuestionChange('text', e.target.value)}
                        className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="أدخل سؤالك"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        صورة السؤال (اختياري)
                      </label>
                      <div className="space-y-2">
                        {questionImagePreview || newQuestion.imagePath ? (
                          <div className="relative">
                            <img
                              src={questionImagePreview || (newQuestion.imagePath ? getImageUrl(newQuestion.imagePath) : '')}
                              alt="معاينة صورة السؤال"
                              className="max-w-full h-auto max-h-64 rounded border border-gray-300"
                            />
                            <button
                              type="button"
                              onClick={handleRemoveImage}
                              className="absolute top-2 left-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                              title="إزالة الصورة"
                            >
                              <XMarkIcon className="h-4 w-4" />
                            </button>
                          </div>
                        ) : (
                          <div>
                            <input
                              type="file"
                              accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                              onChange={handleImageFileChange}
                              disabled={uploadingImage}
                              className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                            />
                            <p className="mt-1 text-xs text-gray-500">
                              الصيغ المدعومة: JPG, JPEG, PNG, GIF, WebP. الحد الأقصى: 3 ميجابايت
                            </p>
                            {uploadingImage && (
                              <p className="mt-1 text-xs text-blue-600">جارٍ رفع الصورة...</p>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        نوع السؤال
                      </label>
                      <select
                        value={newQuestion.type}
                        onChange={(e: React.ChangeEvent<HTMLSelectElement>) => handleQuestionChange('type', e.target.value as 'MultipleChoice' | 'TrueFalse')}
                        className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="MultipleChoice">اختيار متعدد</option>
                        <option value="TrueFalse">صح/خطأ</option>
                      </select>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        الإجابات
                      </label>
                      {newQuestion.type === 'MultipleChoice' && (
                        <div className="mb-3">
                          <p className="text-sm text-gray-600 mb-2">تعبئة الخيارات تلقائياً</p>
                          <div className="flex flex-wrap gap-2">
                            <button
                              type="button"
                              onClick={() => applyChoicePreset('arabic')}
                              className={`px-4 py-2 rounded-lg border text-sm font-medium transition-colors ${
                                choicePreset === 'arabic'
                                  ? 'bg-blue-600 text-white border-blue-600'
                                  : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                              }`}
                            >
                              جعل كل الخيارات أ ب ت ث
                            </button>
                            <button
                              type="button"
                              onClick={() => applyChoicePreset('abcd')}
                              className={`px-4 py-2 rounded-lg border text-sm font-medium transition-colors ${
                                choicePreset === 'abcd'
                                  ? 'bg-blue-600 text-white border-blue-600'
                                  : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                              }`}
                            >
                              ABCD
                            </button>
                          </div>
                        </div>
                      )}
                      <div className="space-y-2">
                        {newQuestion.answers.map((answer, index) => (
                          <div key={index} className="flex items-center space-x-reverse space-x-2">
                            <input
                              type="text"
                              value={answer.text}
                              onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleAnswerChange(index, 'text', e.target.value)}
                              className="flex-1 px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                              placeholder={`إجابة ${index + 1}`}
                              disabled={newQuestion.type === 'TrueFalse'}
                            />
                            <div className="flex items-center">
                              <input
                                type="checkbox"
                                checked={answer.isCorrect}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleAnswerChange(index, 'isCorrect', e.target.checked)}
                                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                              />
                              <span className="mr-2 text-sm text-gray-600">صحيح</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                    
                    <div className="flex justify-end space-x-reverse space-x-2 pt-4">
                      <button
                        onClick={() => {
                          setShowQuestionForm(false);
                          setQuestionImagePreview(null);
                          setNewQuestion(prev => ({ ...prev, imagePath: null }));
                        }}
                        className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300"
                      >
                        إلغاء
                      </button>
                      <button
                        onClick={handleAddQuestion}
                        disabled={uploadingImage}
                        className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        إضافة سؤال
                      </button>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Existing Questions */}
              {isEditing && questions.length > 1 && (
                <p className="text-sm text-gray-500">اضغط السؤال لفتحه. اسحب الصف لتغيير الترتيب، ثم اضغط حفظ.</p>
              )}
              <div className="space-y-2">
              {questions.map((question, questionIndex) => {
                const isOpen = openQuestionIndex === questionIndex;
                return (
                <div
                  key={questionIndex}
                  onDragOver={(event) => handleQuestionDragOver(event, questionIndex)}
                  onDrop={(event) => handleQuestionDrop(event, questionIndex)}
                  className={`bg-white rounded-lg border shadow-sm ${
                    dragOverQuestionIndex === questionIndex && draggedQuestionIndex !== questionIndex
                      ? 'border-blue-400 ring-2 ring-blue-200'
                      : 'border-gray-200'
                  } ${draggedQuestionIndex === questionIndex ? 'opacity-60' : ''}`}
                >
                  <div className="flex items-center gap-2 px-3 py-2">
                    {isEditing && (
                      <div
                        draggable
                        onDragStart={(event) => handleQuestionDragStart(event, questionIndex)}
                        onDragEnd={handleQuestionDragEnd}
                        className="cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-600 shrink-0"
                        title="اسحب لتغيير الترتيب"
                      >
                        <Bars3Icon className="h-5 w-5" />
                      </div>
                    )}
                    <button
                      type="button"
                      onClick={() => {
                        if (questionDragged.current) {
                          questionDragged.current = false;
                          return;
                        }
                        setOpenQuestionIndex(isOpen ? null : questionIndex);
                      }}
                      className="flex flex-1 items-center gap-2 min-w-0 text-right"
                    >
                      <span className="font-medium text-gray-900 shrink-0">سؤال {questionIndex + 1}</span>
                      <span className="truncate text-sm text-gray-600 flex-1">{question.text}</span>
                      <ChevronDownIcon className={`h-4 w-4 shrink-0 text-gray-500 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                    </button>
                    {isEditing && (
                      <button
                        type="button"
                        onMouseDown={(event) => event.stopPropagation()}
                        onClick={() => handleDeleteQuestion(questionIndex)}
                        className="text-red-500 hover:text-red-700 shrink-0"
                        title="حذف السؤال"
                      >
                        <TrashIcon className="h-5 w-5" />
                      </button>
                    )}
                  </div>

                  {isOpen && (
                  <div className="space-y-3 px-4 pb-4 border-t border-gray-100 pt-3">
                    {question.imagePath && (
                      <img
                        src={getImageUrl(question.imagePath) || question.imagePath}
                        alt="صورة السؤال"
                        className="max-w-full h-auto max-h-64 rounded border border-gray-300"
                        onError={(e: any) => {
                          (e.target as HTMLImageElement).style.display = 'none';
                        }}
                      />
                    )}
                    {question.answers.map((answer, answerIndex) => (
                      <div
                        key={answerIndex}
                        className={`p-3 rounded-lg border ${
                          answer.isCorrect
                            ? 'bg-green-50 border-green-200'
                            : 'bg-gray-50 border-gray-200'
                        }`}
                      >
                        <div className="flex items-center">
                          <span className="w-6 h-6 flex items-center justify-center rounded-full border border-gray-300 ml-3">
                            {String.fromCharCode(65 + answerIndex)}
                          </span>
                          <span className={`flex-1 ${answer.isCorrect ? 'font-medium' : ''}`}>
                            {answer.text}
                          </span>
                          {answer.isCorrect && (
                            <span className="mr-3 text-green-600">
                              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                  )}
                </div>
                );
              })}
              </div>
              
              {questions.length === 0 && !showQuestionForm && (
                <div className="text-center text-gray-500 py-8">
                  لا توجد أسئلة متاحة لهذا الاختبار.
                  {isEditing && (
                    <div className="mt-2">
                      <button
                        onClick={() => setShowQuestionForm(true)}
                        className="text-blue-600 hover:text-blue-800"
                      >
                        أضف السؤال الأول
                      </button>
                    </div>
                  )}
                </div>
              )}
                    </div>
          )}

          {/* Attachment section */}
          {lessonData.attachmentUrl && (
            <div className="mt-4">
              <h3 className="text-lg font-semibold mb-2">المرفق</h3>
              <a
                href={lessonData.attachmentUrl}
                      target="_blank" 
                      rel="noopener noreferrer"
                className="text-blue-600 hover:text-blue-800 flex items-center"
              >
                <span className="ml-2">📎</span>
                {lessonData.attachmentTitel || 'تحميل المرفق'}
              </a>
            </div>
          )}

          {/* Attachment inputs when editing */}
          {isEditing && (
            <div className="space-y-4 mt-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  عنوان المرفق
                </label>
                      <input
                        type="text"
                  value={editedData.attachmentTitel || lessonData.attachmentTitel || ''}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleInputChange('attachmentTitel', e.target.value)}
                  className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="أدخل عنوان المرفق"
                      />
                    </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  رابط المرفق
                </label>
                      <input
                  type="text"
                        value={editedData.attachmentUrl || lessonData.attachmentUrl || ''}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleInputChange('attachmentUrl', e.target.value)}
                  className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="أدخل رابط المرفق"
                      />
                </div>
              </div>
            )}
        </div>
      </div>
    </div>
  );
};

export default LessonView; 